import { PAYMENT_TYPES } from '@sdk/account/constants';

import { SUBSCRIPTION_LEVELS } from '../constants';
import config from '../../config';

export default class SubscriptionUtils {
  constructor(Authentication, Utils, sdk) {
    'ngInject';

    Object.assign(this, { Authentication, Utils, sdk });
    this.SUBSCRIPTION_NAMES = sdk('constants').SUBSCRIPTION_NAMES;
  }

  getPricePerUnit(level, isYearly) {
    const cycle = isYearly ? 'yearly' : 'monthly';
    return (
      {
        monthly: {
          [SUBSCRIPTION_LEVELS.PREMIUM]: this.Authentication.user.orgSettings.pricePerUser,
          [SUBSCRIPTION_LEVELS.BUSINESS]: this.Authentication.user.orgSettings.businessPricePerUser,
        },
        yearly: {
          [SUBSCRIPTION_LEVELS.PREMIUM]: this.Authentication.user.orgSettings.pricePerUserYearly,
          [SUBSCRIPTION_LEVELS.BUSINESS]:
            this.Authentication.user.orgSettings.businessPricePerUserYearly,
        },
      }[cycle][level] || this.Authentication.user.orgSettings.pricePerUser
    );
  }

  getSubtotal(billingInfo, level, isYearly) {
    const pricePerUnit = this.getPricePerUnit(level, isYearly);
    return this.Utils.roundNumber(billingInfo.quantity * pricePerUnit);
  }

  getProration(billingInfo, level, ignoreNextCharge = false, isYearly = false) {
    if (!billingInfo.nextChargeAmount || ignoreNextCharge) {
      return 0;
    }

    return (
      this.getTotal(billingInfo, level, ignoreNextCharge, isYearly) -
      this.getDiscount(billingInfo, level, isYearly) -
      this.getSubtotal(billingInfo, level, isYearly)
    );
  }

  getDiscount(billingInfo, level, isYearly) {
    if (billingInfo.paymentType === PAYMENT_TYPES.SHOPIFY_BILLING) {
      return 0;
    }

    if (billingInfo.discountAmount) {
      return -billingInfo.discountAmount;
    }

    if (billingInfo.discountPercentage) {
      return (
        (-this.getSubtotal(billingInfo, level, isYearly) * billingInfo.discountPercentage) / 100
      );
    }

    return 0;
  }

  /**
   * This function returns NET total amount
   *
   * @param {*} billingInfo
   * @param {*} level
   * @param {*} ignoreNextCharge
   * @param {*} isYearly
   * @returns
   */
  getTotal(billingInfo, level, ignoreNextCharge = false, isYearly) {
    let total = 0;
    if (!ignoreNextCharge && billingInfo.subscriptionStartDate && billingInfo.nextChargeAmount) {
      total = billingInfo.nextChargeAmount;
    } else {
      total = Math.max(
        0,
        this.getSubtotal(billingInfo, level, isYearly) +
          this.getDiscount(billingInfo, level, isYearly)
      );
    }

    return this.Utils.roundNumber(total);
  }

  getSubscriptionName(subscriptionLevel) {
    if (this.Authentication.user.org.isToastBilling) {
      return subscriptionLevel ? 'Scheduling Pro' : 'Scheduling Lite';
    }

    if (this.Authentication.isOldPremium) {
      return 'Premium';
    }

    if (typeof subscriptionLevel === 'number') {
      return this.SUBSCRIPTION_NAMES[subscriptionLevel];
    }

    return this.SUBSCRIPTION_NAMES[
      this.Authentication.user?.orgSettings.premiumFeatures[subscriptionLevel]
    ];
  }

  getSubscriptionNameDetailed(subscriptionLevel, isOnTrial) {
    if (this.Authentication.isOldPremium) {
      return 'Old premium';
    }

    if (isOnTrial) {
      return 'In trial';
    }

    return this.getSubscriptionName(subscriptionLevel) || 'Free';
  }

  areYearlySubscriptionsEnabled(billingInfo) {
    return (
      config.YEARLY_SUBSCRIPTIONS_ENABLED &&
      billingInfo.paymentType !== PAYMENT_TYPES.SHOPIFY_BILLING &&
      !this.Authentication.isOldPremium // https://github.com/getsling/sling-issues/issues/4922#issuecomment-925987277
    );
  }
}
